<script lang="ts" setup>
import type { DefineComponent } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Inputable } from '../../../../lib/types/Inputable'
import type { Testable } from '../../../../lib/types/Testable'
import { FormControl, FormField, FormItem } from '../../form'
import AFormMessage from '../../form/FormMessage.vue'
import { RogueInput } from '../../input'
import { Label } from '../../label'
import { TextArea } from '../../textarea'

export interface TextInputProperties extends Testable, Inputable {
  type: 'text' | 'password' | 'email' | 'number' | 'textarea' | 'date' | 'time'
  disabled?: boolean
  readonly?: boolean
  label?: string
  limit?: number
  rows?: number
  autofocus?: boolean
  placeholder?: string
  icon?: DefineComponent
  /** @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#token_list_tokens */
  autocomplete?: boolean | string | undefined
  clearable?: boolean
  clearLabel?: string
  step?: string
  hideDetails?: boolean
  preventDefaultEvents?: boolean
}

const { t } = useI18n({
  messages: {
    en: {
      'enter {item}': "@.capitalize:{'{item}'}",
    },
    nl: {
      'enter {item}': "Vul een @.lower:{'{item}'} in",
    },
  },
})

// Props
const properties = defineProps<TextInputProperties>()
const value = defineModel<string>({ default: '' })
// end Props

// Emits
const emit = defineEmits<{
  (event: 'clear'): void
}>()
// end Emits

// Refs
// end Refs

// Computed
// end Computed

// Functions
function onClear() {
  emit('clear')
}
// end Functions

// Async Api functions
// end Async Api functions

// Lifecycle Hooks / Init
// end Lifecycle Hooks / Init
</script>

<template>
  <!-- eslint-disable-next-line vue/no-unused-vars -->
  <FormField v-slot="{ componentField }" :model-value="value" :name="name || ''">
    <!-- eslint-disable vuejs-accessibility/no-autofocus -->
    <!-- eslint-disable vuejs-accessibility/form-control-has-label -->
    <!-- eslint-disable vuejs-accessibility/label-has-for -->
    <FormItem>
      <slot name="label">
        <Label v-if="label !== undefined" :for="id">{{ label }} {{ required ? '*' : '' }}</Label>
      </slot>
      <FormControl>
        <template v-if="type === 'textarea'">
          <TextArea
            :id="id"
            v-model="value"
            :autocomplete="autocomplete"
            :autofocus="autofocus"
            :class="properties.class"
            :data-test
            :disabled="disabled"
            :error="error"
            :limit="limit"
            :placeholder="placeholder === undefined ? t('enter {item}', { item: label }) : placeholder"
            :readonly="readonly"
            :rows="rows"
          />
        </template>

        <template v-else-if="type === 'text' || 'number' || 'password' || 'email' || 'date' || 'time'">
          <RogueInput
            :id="id"
            v-model="value"
            :autocomplete="autocomplete"
            :autofocus="autofocus"
            :class="properties.class"
            :clear-label="clearLabel"
            :clearable="clearable"
            :data-test
            :disabled="disabled"
            :error="error"
            :limit="limit"
            :placeholder="placeholder === undefined ? t('enter {item}', { item: label }) : placeholder"
            :prevent-default-events="preventDefaultEvents"
            :readonly="readonly"
            :step="step"
            :type="type"
            @clear="onClear"
          />
        </template>
      </FormControl>
      <AFormMessage v-if="!hideDetails" />
    </FormItem>
  </FormField>
</template>

<style lang="scss" scoped></style>
